import { supabase, applyOrgFilter, getActiveOrgOverride } from '../supabaseClient'
import { normalizeSportValue } from '../../constants/sports'
import { getCurrentStaff } from './auth'

export const getCategorias = async (options = {}) => {
  const { includeInactive = false } = options;
  let query = supabase.from('categorias').select('*').order('edad_max', { ascending: true });
  if (!includeInactive) {
    query = query.eq('activo', true);
  }
  query = applyOrgFilter(query);
  const { data, error } = await query;
  return { data, error };
};

export const createCategoria = async (categoriaData) => {
  try {
    if (!categoriaData?.deporte) {
      return { data: null, error: { code: 'DEPORTE_OBLIGATORIO', message: 'El deporte es obligatorio' } };
    }
    const { data: currentStaff } = await getCurrentStaff();
    // Sin org no se adivina: caer a la org por defecto guardaba la categoria en otra org.
    const orgId = getActiveOrgOverride() || categoriaData.org_id || currentStaff?.org_id;
    if (!orgId) {
      return { data: null, error: { code: 'ORG_OBLIGATORIA', message: 'No se pudo saber tu organización. Recarga la página e intenta de nuevo.' } };
    }
    const payload = {
      ...categoriaData,
      deporte: normalizeSportValue(categoriaData.deporte),
      activo: categoriaData.activo !== false,
      org_id: orgId
    };
    const { data, error } = await supabase
      .from('categorias')
      .insert(payload)
      .select()
      .single();
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
};

export const updateCategoria = async (categoriaId, categoriaData) => {
  try {
    const payload = { ...categoriaData };
    if (payload.deporte) {
      payload.deporte = normalizeSportValue(payload.deporte);
    }
    const { data, error } = await supabase
      .from('categorias')
      .update(payload)
      .eq('id', categoriaId)
      .select('id');

    if (error) {
      return { data: null, error };
    }

    if (!Array.isArray(data) || data.length === 0) {
      return {
        data: null,
        error: {
          code: 'CATEGORIA_NO_ACTUALIZADA',
          message:
            'No se guardaron cambios. Revisa permisos RLS o que la categoría siga activa.'
        }
      };
    }

    const updated = { data: data[0], error: null };

    const { data: groupLinks, error: linkError } = await supabase
      .from('grupos_categorias')
      .select('grupo_id')
      .eq('categoria_id', categoriaId);

    if (!linkError && groupLinks && groupLinks.length > 0) {
      const groupIds = Array.from(new Set(groupLinks.map((row) => row.grupo_id).filter(Boolean)));
      await Promise.all(
        groupIds.map((groupId) =>
          supabase.functions.invoke('recalculate-player-metadata', {
            body: { group_id: groupId }
          })
        )
      );
    }

    return updated;
  } catch (error) {
    return { data: null, error };
  }
};

export const setCategoriaActiva = async (categoriaId, activo) => {
  return updateCategoria(categoriaId, { activo });
};

export const removeCategoriaFromGroups = async (categoriaId) => {
  try {
    const { data: groupLinks, error: linkError } = await supabase
      .from('grupos_categorias')
      .select('grupo_id')
      .eq('categoria_id', categoriaId);

    if (linkError) {
      return { data: null, error: linkError };
    }

    const groupIds = Array.from(new Set((groupLinks || []).map((row) => row.grupo_id).filter(Boolean)));

    const { error: deleteError } = await supabase
      .from('grupos_categorias')
      .delete()
      .eq('categoria_id', categoriaId);

    if (deleteError) {
      return { data: null, error: deleteError };
    }

    if (groupIds.length > 0) {
      await Promise.all(
        groupIds.map((groupId) =>
          supabase.functions.invoke('recalculate-player-metadata', {
            body: { group_id: groupId }
          })
        )
      );
    }

    return { data: { groupIds }, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

export const getCategoriaGroupLinks = async (categoriaId) => {
  try {
    const { data, error } = await supabase
      .from('grupos_categorias')
      .select('grupo_id, grupos(nombre_del_grupo)')
      .eq('categoria_id', categoriaId);

    if (error) return { data: null, error };

    const groupIds = Array.from(new Set((data || []).map((row) => row.grupo_id).filter(Boolean)));
    const groupNames = Array.from(
      new Set(
        (data || [])
          .map((row) => row.grupos?.nombre_del_grupo)
          .filter(Boolean)
      )
    );
    return { data: { groupIds, groupNames }, error: null };
  } catch (error) {
    return { data: null, error };
  }
};
